import { PoiMediaType } from '@core/dashboard/components/DashboardContent/interfaces';
import { IMillicastStream } from '@core/dashboard/providers/MytaverseEventProvider/hooks/millicast/interfaces';
import { IDolbyMediaStream } from '@core/dashboard/components/DashboardContent/Dolby/interfaces';
import { IStartStreamSessionOutput, StreamSessionStatus } from './gamecast';
import { IZoomParticipantPositionData } from '@core/communications/providers/interfaces';
// Type-only: a value import would pull the event interfaces barrel in here.
import type { EventStatus } from './event';

export interface IWebSocketConnectionInfo {
  connectionId: string;
  participantId: string;
  loginToken: string;
  eventId?: string;
  roomId?: string;
  region?: string;
  gameSessionId?: string | null;
  spatialType?: SpatialType;
  screenName?: string;
  clientType?: string;
  pairConnectionId?: string;
  dolbySpatialAudioScale?: number;
  muted?: boolean;
  category?: string;
  msg?: string;
}

export enum EFullscreenType {
  Screen = 'SCREEN',
  Camera = 'CAMERA',
}

export enum SpatialType {
  SpatialMeeting = 'SPATIAL_MEETING',
  SpatialAudio = 'SPATIAL_AUDIO',
  OneToMany = 'ONE_TO_MANY',
  DistanceAudio = 'DISTANCE_AUDIO',
  None = 'NONE',
}

export enum PlayerViewUEEnum {
  FirstPerson = 'firstPerson',
  ThirdPerson = 'thirdPerson',
}

export interface PlayerPosition {
  CurrentGameSessionId?: string;
  eventId?: string;
  roomId?: string;
  x: number;
  y: number;
  z: number;
  r: number;
}

export interface ILastWebsocketJsonMessage<T = WebsocketAction> {
  data: IWebSocketConnectionInfo;
  action: T;
  timestamp?: string;
  message?: WebsocketMessage;
  gameSessionId?: string;
  eventId?: string;
  roomId?: string;
  region?: string;
  regionName?: string;
  regionSpatialType?: SpatialType;
  isSeatRegion?: boolean;
  screenName?: string;
  participantId?: string;
  id?: string;
  width?: string | number;
  height?: string | number;
  gamecastStreamSession?: IStartStreamSessionOutput | null;
  status?: StreamSessionStatus;
  sessionKey?: string;
  fileName?: string;
  asset?: string;
  downloadUrl?: string;
  mediaType?: PoiMediaType;
  muted?: boolean;
  conferenceId?: string;
  mutedBy?: string;
  spatialType?: SpatialType;

  type?: EFullscreenType | string;
  nearestParticipants?: IZoomParticipantPositionData[];
  x?: number;
  y?: number;
  z?: number;
  r?: number;
  mediaStreamId?: string;
  usersinscreen?: string[] | undefined;
  playerPosition?: PlayerPosition;
  dolbySpatialAudioScale?: number;
  // follow me
  followerId?: string;
  ownerId?: string;
  isEventMessage?: boolean;
  followerIds?: string[];
  url: string;
  millicastStreams?: IMillicastStream[];
  dolbyMediaStreams?: IDolbyMediaStream[];
  zoomWebcamScreenId?: string;
  zoomScreenScreenId?: string;
  /** SYNC_DATA_UE / UE: same role as zoomScreenScreenId (no "zoom" in payload). */
  videoScreenScreenId?: string;
  videoWebcamScreenId?: string;
  /** SYNC_DATA_UE: GameLift session ARN on message root. */
  CurrentGameSessionId?: string;
  audioTrigger?: {
    audioTriggerId?: string;
    videoScreenScreenId?: string;
    videoWebcamScreenId?: string;
    audioType?: string;
    spatialPlayers?: unknown[];
    isSeatRegion?: boolean;
    isDisableWebCamera?: boolean;
  };
  showExitButton?: boolean;

  isHideWebUI?: boolean;
  isHideWebUIExceptAssistant?: boolean;
  isHideAllExceptTopBar?: boolean;

  playerView?: PlayerViewUEEnum;
  zoomScreenStreams?: IDolbyMediaStream[];
  zoomWebcamStreams?: IDolbyMediaStream[];
  speaking?: boolean;
  raisedHand?: boolean;
  worldSoundLevel?: number;
  videoToken?: string;
  videoConferenceName?: string;
  videoUser?: Record<string, unknown>;
  videoOwnerWebcamStream?: {
    participantId?: string;
    videoUserId?: string;
  };
  avatar?: unknown;
  isUEModalActionInProgress?: boolean;
  tag?: string;
  payload?: any;
  name?: string;
  attached?: boolean;
  handRaised?: boolean;
  handRaisedAt?: number;
  poiId?: string;
  hotspotId?: string;
  // EventReleaseStatusChanged payload fields
  eventReleaseId?: string;
}

export interface IParticipantPositionMessage {
  timestamp: number;
  eventId: string;
  roomId: string;
  gameSessionId: string;
  regionName?: string;
  regionSpatialType?: SpatialType;
  x: number;
  y: number;
  z: number;
  r: number;
  zoomWebcamScreenId?: string;
  zoomScreenScreenId?: string;
  isHideWebUI?: boolean;
  isHideWebUIExceptAssistant?: boolean;
  isHideAllExceptTopBar?: boolean;
  nearestParticipants?: IZoomParticipantPositionData[];
  isSeatRegion: boolean;
}

export type LastWebsocketJsonMessageType = ILastWebsocketJsonMessage | null;

export enum WebsocketAction {
  ConnectionInfo = 'CONNECTION_INFO',
  ShowSurvey = 'SHOW_SURVEY',
  JoinedToRoom = 'JOINED_TO_ROOM',
  LeaveRoom = 'LEAVE_ROOM',
  LeftRoom = 'LEFT_ROOM',
  JoinedToEvent = 'JOINED_TO_EVENT',
  ParticipantPosition = 'PARTICIPANT_POSITION',
  SyncDataUe = 'SYNC_DATA_UE',
  AssistantIcon = 'ASSISTANT_ICON',
  LeftEvent = 'LEFT_EVENT',
  ExitEvent = 'EXIT_EVENT',
  JoinToRegion = 'JOIN_TO_REGION',
  JoinedToRegion = 'JOINED_TO_REGION',
  TeleportToLocation = 'TELEPORT_TO_LOCATION',
  LeaveRegion = 'LEAVE_REGION',
  LeftRegion = 'LEFT_REGION',
  ChangeRoomScale = 'CHANGE_ROOM_SCALE',
  BrowserUrlRequest = 'BROWSER_URL_REQUEST',
  MuteParticipant = 'MUTE_PARTICIPANT',
  ShowParticipantInfo = 'SHOW_PARTICIPANT_INFO',
  SendToBriefcase = 'SEND_TO_BRIEFCASE',
  ChangeParticipantProfile = 'CHANGE_PARTICIPANT_PROFILE',
  ParticipantRoleUpdated = 'PARTICIPANT_ROLE_UPDATED',
  MessageToWebapp = 'MESSAGE_TO_WEBAPP',
  MessageToRoom = 'MESSAGE_TO_ROOM',
  CloseGame = 'CLOSE_GAME',
  StartShareVideoRequest = 'START_SHARE_VIDEO_REQUEST',
  StopShareVideoRequest = 'STOP_SHARE_VIDEO_REQUEST',

  StartShareScreenRequest = 'START_SCREEN_VIDEO_REQUEST',
  StopShareScreenRequest = 'STOP_SCREEN_VIDEO_REQUEST',

  ShowMillicastScreenBroadcastingDataRequest = 'SHOW_MILLICAST_SCREEN_BROADCASTING_DATA_REQUEST',

  Ue5WsConnected = 'UE5_WS_CONNECTED',
  Ue5WsDisconnected = 'UE5_WS_DISCONNECTED',
  FirstPersonViewRegion = 'FIRST_PERSON_VIEW_REGION',
  FreePersonViewRegion = 'FREE_PERSON_VIEW_REGION',
  ParticipantMutedState = 'PARTICIPANT_MUTED_STATE',
  MuteParticipantState = 'MUTE_PARTICIPANT_STATE',
  MuteConference = 'MUTE_CONFERENCE',
  StopDolbyScreenSharing = 'STOP_DOLBY_SCREEN_SHARING',
  ParticipantCameraPosition = 'PARTICIPANT_VIDEO_POSITION',
  ToggleLevelEditor = 'TOGGLE_LEVEL_EDITOR',

  SyncDataWeb = 'SYNC_DATA_WEB',

  EnableDroneCamera = 'ENABLE_DRONE_CAMERA',
  DisableDroneCamera = 'DISABLE_DRONE_CAMERA',

  Hoverboard = 'HOVERBOARD',
  LaserPointer = 'LASER_POINTER',
  Magnifier = 'MAGNIFIER',
  BeamMeUp = 'BEAM_ME_UP',
  TeleportToIdLocation = 'TELEPORT_TO_ID_LOCATION',
  SetupKeyboard = 'SETUP_KEYBOARD',
  FeatureFlags = 'FEATURE_FLAGS',

  TeleportToParticipant = 'TELEPORT_TO_PARTICIPANT',
  TeleportToRoom = 'TELEPORT_TO_ROOM',
  OwnerTeleport = 'OWNER_TELEPORT_TO_ROOM',

  PlayVideo = 'PLAY_VIDEO',

  FollowPlayer = 'FOLLOW_PLAYER',
  InviteFollowingByAdmin = 'INVITE_FOLLOWING_BY_ADMIN',
  StopFollowingByAdmin = 'STOP_FOLLOWING_BY_ADMIN',
  CancelInviteFollowingByAdmin = 'CANCEL_INVITE_FOLLOWING_BY_ADMIN',
  AcceptFollowing = 'ACCEPT_FOLLOWING',
  DisengageFollowing = 'DISENGAGE_FOLLOWING',
  StopFollowing = 'STOP_FOLLOWING',
  OpenTeleportModal = 'OPEN_TELEPORT_MODAL',
  CloseTeleportModal = 'CLOSE_TELEPORT_MODAL',
  TeleportToRegion = 'TELEPORT_TO_REGION',
  TeleportFinished = 'TELEPORT_FINISHED',
  ClientReady = 'CLIENT_READY',
  ShowNotification = 'SHOW_NOTIFICATION',
  TerminateSession = 'TERMINATE_SESSION',
  ShowDemoNotification = 'SHOW_DEMO_NOTIFICATION',
  ShowWebNotification = 'SHOW_WEB_NOTIFICATION',

  ToggleExitButton = 'TOGGLE_EXIT_BUTTON',

  AvatarRequested = 'SELECT_AVATAR_REQUEST',
  AvatarReceived = 'AVATAR_RECEIVED',
  AvatarSelect = 'SELECT_AVATAR',

  MytaverseAction = 'MYTA_EVENT',
  GamecastStreamSession = 'GAMECAST_STREAM_SESSION',

  GamecastSessionTerminated = 'GAMECAST_SESSION_TERMINATED',

  // Create Avatar
  PreloadAvatar = 'PRELOAD_AVATAR',
  ReloadAvatar = 'CHANGE_AVATAR',

  // UE Verbosity
  UEError = 'UE_ERROR',
  UEWarn = 'UE_WARN',
  UEDebug = 'UE_DEBUG',
  UEInfo = 'UE_INFO',
  FullscreenSharing = 'START_FULLSCREEN_SHARING',
  OpenOfficeHours = 'OPEN_OFFICE_HOURS',

  // Map Modal
  ShowHubMap = 'SHOW_HUB_MAP',
  HideHubMap = 'SHOW_HUB_MAP',
  MButtonClick = 'M_BUTTON_CLICK',

  //3D Assets
  Asset3DLoad = '3D_ASSET_LOAD',
  Asset3DStop = '3D_ASSET_STOP',
  Asset3DSetDimension = '3D_ASSET_SET_DIMENSION',
  Asset3DReady = '3D_ASSET_READY',
  Asset3DNewLimits = '3D_ASSET_CHANGE_LIMITS',
  Asset3DNewFailed = '3D_ASSET_LOAD_FAILED',
  Asset3DExploration = '3D_ASSET_ALLOW_EXPLORATION',
  Asset3DMaxSize = '3D_ASSET_MAX_SIZE',

  EventStage = 'EVENT_STAGE',

  TakeScreenshotRequest = 'TAKE_SCREENSHOT_REQUEST',
  TakeScreenshotFinish = 'TAKE_SCREENSHOT_FINISH',
  TakeScreenshotFailed = 'TAKE_SCREENSHOT_FAILED',

  ExternalKeyboard = 'EXTERNAL_KEYBOARD',

  CustomisationItemSelected = 'CUSTOMISATION_ITEM_SELECTED',
  RaiseHandState = 'RAISE_HAND_STATE',

  StopEntireSession = 'STOP_ENTIRE_SESSION',

  MovementCollisionIdentified = 'MOVEMENT_COLLISION_IDENTIFIED',

  AssistantScriptUpdated = 'ASSISTANT_SCRIPT_UPDATED',

  EventSettingsUpdated = 'EVENT_SETTINGS_UPDATED',

  ObjectPoiClosed = '3D_POI_CLOSED',
  Hotspot3DClosed = '3D_HOTSPOT_CLOSED',
  ShowHotspotPreview = 'SHOW_HOTSPOT_PREVIEW',
  StartHotspotPlace2D = 'START_HOTSPOT_PLACE_2D',
  StartHotspotPlace3D = 'START_HOTSPOT_PLACE_3D',
  FinishHotspotPlace2D = 'FINISH_HOTSPOT_PLACE_2D',
  FinishHotspotPlace3D = 'FINISH_HOTSPOT_PLACE_3D',

  SpotFullscreenView = 'SPOT_FULLSCREEN_VIEW',

  EventReleaseStatusChanged = 'EVENT_RELEASE_STATUS_CHANGED',
  EventChanged = 'EVENT_CHANGED',
}

/**
 * Allow-listed event fields the backend broadcasts over `EVENT_CHANGED`.
 * Deliberately not the whole record: the carousel reads experiences through
 * GET /api/v1/profile/events, which joins the release and normalizes asset
 * URLs, so a raw item is a different shape and cannot replace a loaded card.
 */
export interface IEventChangedFields {
  status: EventStatus;
  eventReleaseId: string | null;
  clientVersion: string | null;
  clientChangeset: string | null;
  isPublished: boolean;
}

export interface IEventChangedPayload {
  action: WebsocketAction.EventChanged;
  eventId: string;
  worldId: string;
  changed: (keyof IEventChangedFields)[];
  event: IEventChangedFields;
  timestamp: number;
}

export enum WebsocketMessage {
  ShowControls = 'SHOW_CONTROLS',
  HideControls = 'HIDE_CONTROLS',
}

export enum WebsocketErrorCode {
  CloseAbnormal = 1006,
}

export enum WebSocketUECategory {
  UEAvatarLoadError = 'UE_ERROR_AVATAR_LOAD',
}
